import { LineItem } from 'shared/types/cart';
import { Money } from 'types/entity/product';

export function addDaysFromTodayAndFormat(n: number) {
  const now = new Date();
  const result = new Date(now);
  result.setDate(result.getDate() + n);
  return formatDate(result);
}

function formatDate(date: Date) {
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: '2-digit',
    day: '2-digit',
  })
    .format(date)
    .replace(',', ''); // remove comma after weekday
}

export function separateMediaAndPrintBuilderProducts(lineItems: LineItem[] = []): [LineItem[], LineItem[][]] {
  if (lineItems.length === 0) return [[], []];

  const mediaClipItems = lineItems?.filter(
    (item) =>
      typeof (item?.custom as any)?.fields.mediaClipProjectId === 'string' &&
      !(item?.custom as any)?.fields?.parentLineItem,
  );

  const printBuilderItems = lineItems?.filter((item) => typeof item?.imageMetadata?.id === 'string');

  return [mediaClipItems, groupPrintBuilderItemsByProductSlug(printBuilderItems)];
}

function groupPrintBuilderItemsByProductSlug(items: LineItem[]): LineItem[][] {
  const groups: Record<string, LineItem[]> = items.reduce(
    (acc, item) => {
      if (!item.productSlug) return acc;
      if (acc[item.productSlug] === undefined || acc[item.productSlug] === null) {
        acc[item.productSlug] = [];
      }
      acc[item.productSlug].push(item);
      return acc;
    },
    {} as Record<string, LineItem[]>,
  );

  return Object.values(groups);
}

export function groupLineItemsBySku(items?: LineItem[]): LineItem[] {
  if (!items || items.length === 0) return [];

  const groups: Record<string, LineItem[]> = items.reduce(
    (acc, item) => {
      const sku = item.variant?.sku;
      if (!sku) return acc;
      acc[sku] = acc[sku] ?? [];
      acc[sku].push(item);
      return acc;
    },
    {} as Record<string, LineItem[]>,
  );

  const groupedResult = Object.values(groups).map((lineItems) => {
    const first = lineItems[0];

    const totalCount = lineItems.reduce((acc, curr) => acc + (curr?.count ?? 0), 0);
    const mediaCount = lineItems.reduce((acc) => acc + 1, 0);

    return {
      name: first?.name ?? '',
      variant: first?.variant,
      count: totalCount,
      mediaCount,
      price: first?.price,
      originalPrice: first?.originalPrice,
      discountedPrice: first?.discountedPrice,
      discountedPricePerCount: first?.discountedPricePerCount,
      ...(first?.discountedPrice ? { discountedPrice: first.discountedPrice } : {}),
      totalPrice: getTotalPriceOfAllSku(lineItems),
      imageMetadata: first?.imageMetadata,
      custom: first?.custom,
      reorder: {
        quantity: totalCount,
        distributionChannel: first?.distributionChannel,
        productSku: first?.variant?.sku,
      },
      _url: first?._url,
      productId: first?.productId,
      id: first?.lineItemId,
      originalTotalPrice: first?.originalTotalPrice,
      allDiscounts: first?.allDiscounts,
    };
  });
  return groupedResult;
}

export function getTotalPriceOfAllSku(lineItems: LineItem[]): Money {
  if (lineItems.length === 0) {
    return { fractionDigits: undefined, centAmount: 0, currencyCode: undefined };
  }

  // Find the first defined currency/fractionDigits to set the baseline.
  const baseline = lineItems.find(
    (p) => p?.totalPrice?.currencyCode && typeof p?.totalPrice?.fractionDigits === 'number',
  );

  // If no baseline is found, treat all as undefined but still sum centAmount
  const baselineCurrency = baseline?.totalPrice?.currencyCode;
  const baselineFractionDigits = baseline?.totalPrice?.fractionDigits;

  // Validate consistency
  for (const p of lineItems) {
    if (p?.totalPrice?.currencyCode && baselineCurrency && p?.totalPrice?.currencyCode !== baselineCurrency) {
      throw new Error(`Currency mismatch: expected ${baselineCurrency} but found ${p?.totalPrice?.currencyCode}`);
    }
    if (
      typeof p?.totalPrice?.fractionDigits === 'number' &&
      typeof baselineFractionDigits === 'number' &&
      p?.totalPrice?.fractionDigits !== baselineFractionDigits
    ) {
      throw new Error(
        `fractionDigits mismatch: expected ${baselineFractionDigits} but found ${p?.totalPrice?.fractionDigits}`,
      );
    }
  }

  const totalCentAmount = lineItems.reduce((sum, p) => sum + (p?.totalPrice?.centAmount ?? 0), 0);

  return {
    fractionDigits: baselineFractionDigits,
    centAmount: totalCentAmount,
    currencyCode: baselineCurrency,
  };
}

export function formatISO(isoString: Date, isType?: string) {
  const d = new Date(isoString);
  const parts = new Intl.DateTimeFormat('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZoneName: 'short',
  }).formatToParts(d);

  const pick = (t: string) => parts.find((p) => p.type === t)?.value;
  return `${pick('month')}/${pick('day')}/${pick('year')}${isType === 'isConfirm' ? ',' : ' -'} ${pick('hour')}:${pick('minute')} ${pick('dayPeriod')} (${pick('timeZoneName')})`;
}

export function formatCompact(dateInput: string | Date) {
  const d = new Date(dateInput);

  const parts = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: '2-digit',
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).formatToParts(d);

  const get = (t: string) => parts.find((p) => p.type === t)?.value ?? '';

  return `${get('weekday')} ${get('month')}/${get('day')}, ${get('hour')}:${get('minute')} ${get('dayPeriod').toLowerCase()}`;
}

export function toUtcMidnight(d: Date) {
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
}

export function getChildLineItems(lineItems: LineItem[] | undefined, parentLineItem: string | undefined): LineItem[] {
  if (!lineItems || !parentLineItem) return [];
  return lineItems.filter((item) => item?.custom?.fields?.parentLineItem === parentLineItem);
}

export function getParentLineItems(lineItems: LineItem[]): LineItem[] {
  return lineItems?.filter((item) => !item?.custom?.fields?.parentLineItem);
}

type CartItemCount = {
  count?: number;
  custom?: { fields?: { parentLineItem?: string } };
};

type CartWithItemCounts = {
  lineItems?: CartItemCount[];
};

export function getTotalItemsOfCarts(carts: CartWithItemCounts[] = []): number {
  return carts.reduce((total, cart) => {
    const parentLineItems = cart.lineItems?.filter((item) => !item.custom?.fields?.parentLineItem) ?? [];
    return total + parentLineItems.reduce((cartTotal, lineItem) => cartTotal + (lineItem.count ?? 0), 0);
  }, 0);
}

export function getImageMetadataIdsFromCustomFields(lineItem: {
  custom?: { fields?: { imageMetadataIds?: unknown } };
}): string[] {
  const imageMetadataIds = lineItem?.custom?.fields?.imageMetadataIds;

  if (Array.isArray(imageMetadataIds)) {
    return imageMetadataIds.filter((id): id is string => typeof id === 'string' && id.length > 0);
  }

  if (typeof imageMetadataIds === 'string') {
    try {
      const parsed = JSON.parse(imageMetadataIds);
      if (Array.isArray(parsed)) {
        return parsed.filter((id): id is string => typeof id === 'string' && id.length > 0);
      }
    } catch {
      return imageMetadataIds ? [imageMetadataIds] : [];
    }
  }

  return [];
}

const addMoney = (a?: Money, b?: Money): Money => {
  return {
    currencyCode: a?.currencyCode || b?.currencyCode,
    fractionDigits: a?.fractionDigits ?? b?.fractionDigits,
    centAmount: (a?.centAmount || 0) + (b?.centAmount || 0),
  };
};

const multiplyMoney = (money: Money | undefined, factor: number): Money | undefined => {
  if (!money) return money;
  return { ...money, centAmount: (money.centAmount || 0) * factor };
};

export const mergeLineItems = (item: LineItem, childLineItems: LineItem[]): LineItem => {
  const parentCount = item.count || 1;

  return childLineItems.reduce<LineItem>((acc, child) => {
    const childCount = child.count || 0;
    const scaleFactor = childCount / parentCount;

    const scaledDiscountedPricePerCount = child.discountedPricePerCount?.map((entry) => ({
      ...entry,
      discountedPrice: entry.discountedPrice
        ? { ...entry.discountedPrice, value: multiplyMoney(entry.discountedPrice.value, childCount) as Money }
        : entry.discountedPrice,
    }));

    return {
      ...acc,
      count: (acc.count || 0) + childCount,

      price: addMoney(acc.price, multiplyMoney(child.price, scaleFactor)),

      totalPrice: addMoney(acc.totalPrice, child.totalPrice),

      totalDiscount: addMoney(acc.totalDiscount, child.totalDiscount),

      discountedPrice: child.discountedPrice
        ? {
            value: addMoney(acc.discountedPrice?.value, multiplyMoney(child.discountedPrice.value, childCount)),
            discount: item.discountedPrice?.discount ?? child.discountedPrice.discount,
          }
        : acc.discountedPrice,

      discountedPricePerCount: [...(acc.discountedPricePerCount || []), ...(scaledDiscountedPricePerCount || [])],

      originalPrice: addMoney(acc.originalPrice, multiplyMoney(child.originalPrice, scaleFactor)),

      originalTotalPrice: addMoney(acc.originalTotalPrice, child.originalTotalPrice),
    };
  }, item);
};

export function isParentLineItem(item: LineItem): boolean {
  return !item?.custom?.fields?.parentLineItem;
}

export function isChildLineItem(item: LineItem): boolean {
  return Boolean(item?.custom?.fields?.parentLineItem);
}

export function hasChildren(lineItems: LineItem[] | undefined, parentLineItem: string | undefined): boolean {
  if (!lineItems || !parentLineItem) return false;
  return lineItems.some((item) => item?.custom?.fields?.parentLineItem === parentLineItem);
}
